
import React, {
  useState,
} from "react"

import PulseLoader from "react-spinners/PulseLoader"

import {
  ArrowPathIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ClockIcon,
  CheckCircleIcon,
  ClipboardDocumentListIcon,
} from "@heroicons/react/24/outline"

import useTitle from "@/hooks/useTitle"

import {
  useGetMyOrgIntentionResponsesQuery,
} from "@/features/orgIntentionBoards/orgIntentionResponsesApiSlice"


/*
|--------------------------------------------------------------------------
| My Intention - 27 Sep 2026
|--------------------------------------------------------------------------
|
| Author - Atul Pandey
|
| Purpose:
|
| Allow a Trinity User to revisit their original submitted
| Intention Board response and personal responsiveness metrics.
|
| Uses authenticated personal endpoint.
|
| No userId / candidateId / intentionBoardId passed by frontend.
|
| Read only.
|
*/


/*
|--------------------------------------------------------------------------
| 01 - Format Date
|--------------------------------------------------------------------------
*/

const formatDateTime = (
  value,
  timezone
) => {

  if (!value) return "Not available"

  const date =
    new Date(value)

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return "Not available"
  }

  try {

    return new Intl.DateTimeFormat(
      "en-US",
      {

        month: "short",
        day: "numeric",
        year: "numeric",

        hour: "numeric",
        minute: "2-digit",

        timeZone:
          timezone ||
          Intl.DateTimeFormat()
            .resolvedOptions()
            .timeZone,

      }
    ).format(date)

  } catch {

    return date.toLocaleString()

  }

}


/*
|--------------------------------------------------------------------------
| 02 - Format Duration
|--------------------------------------------------------------------------
|
| Backend returns minutes rounded to one decimal.
|
*/

const formatDuration = value => {

  if (
    value === null ||
    value === undefined ||
    !Number.isFinite(
      Number(value)
    )
  ) {
    return "Not available"
  }

  const minutes =
    Number(value)

  if (minutes < 0) {
    return "Not available"
  }

  if (
    minutes > 0 &&
    minutes < 1
  ) {
    return "< 1 min"
  }

  if (minutes < 1) {
    return "0 min"
  }

  const totalMinutes =
    Math.round(minutes)

  const days =
    Math.floor(
      totalMinutes / 1440
    )

  const hours =
    Math.floor(
      (totalMinutes % 1440) / 60
    )

  const remainingMinutes =
    totalMinutes % 60

  const parts = []

  if (days > 0) {
    parts.push(`${days}d`)
  }

  if (hours > 0) {
    parts.push(`${hours}h`)
  }

  if (
    remainingMinutes > 0 ||
    parts.length === 0
  ) {
    parts.push(
      `${remainingMinutes} min`
    )
  }

  return parts.join(" ")

}


/*
|--------------------------------------------------------------------------
| 03 - Format Deadline Lead
|--------------------------------------------------------------------------
*/

const formatDeadlineLead = value => {

  if (
    value === null ||
    value === undefined ||
    !Number.isFinite(
      Number(value)
    )
  ) {
    return "Not available"
  }

  const minutes =
    Number(value)

  if (minutes < 0) {

    return `${formatDuration(
      Math.abs(minutes)
    )} after deadline`

  }

  return `${formatDuration(minutes)} before deadline`

}


/*
|--------------------------------------------------------------------------
| 04 - Metric Card
|--------------------------------------------------------------------------
*/

const MetricCard = ({
  label,
  value,
  note,
}) => (

  <div className="bg-gray-50 rounded-lg p-4 min-w-0">

    <p className="text-[10px] font-bold uppercase text-gray-500">
      {label}
    </p>

    <p className="text-xl font-bold text-gray-900 mt-2 break-words">
      {value}
    </p>

    {note && (

      <p className="text-[9px] text-gray-500 mt-2">
        {note}
      </p>

    )}

  </div>

)


/*
|--------------------------------------------------------------------------
| 05 - Render My Answer
|--------------------------------------------------------------------------
*/

const MyAnswer = ({
  question,
}) => {

  const questionType =
    question?.questionType

  const selectedOptionDetails =
    question?.selectedOptionDetails || []

  const selectedOptions =
    question?.selectedOptions || []


  /*
  |--------------------------------------------------------------------------
  | Unanswered
  |--------------------------------------------------------------------------
  */

  if (
    question?.isAnswered !== true
  ) {

    return (

      <p className="text-xs italic text-gray-400">
        No response submitted for this question.
      </p>

    )

  }


  /*
  |--------------------------------------------------------------------------
  | Reflection / Text
  |--------------------------------------------------------------------------
  */

  if (
    [
      "reflection",
      "short_text",
      "long_text",
    ].includes(questionType)
  ) {

    return (

      <p className="text-xs text-gray-700 whitespace-pre-wrap break-words leading-relaxed">
        {question.responseText || "No text response."}
      </p>

    )

  }


  /*
  |--------------------------------------------------------------------------
  | Single / Multi Select
  |--------------------------------------------------------------------------
  */

  if (
    [
      "single_select",
      "multi_select",
    ].includes(questionType)
  ) {

    const options =
      selectedOptionDetails.length > 0
        ? selectedOptionDetails.map(
            item =>
              item.optionLabel ||
              item.optionValue
          )
        : selectedOptions


    return (

      <div className="flex flex-wrap gap-2">

        {options.map(
          (option, index) => (

            <span
              key={`${option}-${index}`}
              className="inline-flex bg-orange-50 border border-orange-100 rounded-lg px-3 py-2 text-xs font-semibold text-orange-800"
            >
              {option}
            </span>

          )
        )}

      </div>

    )

  }


  /*
  |--------------------------------------------------------------------------
  | Checkbox Confirmation
  |--------------------------------------------------------------------------
  */

  if (
    questionType ===
    "checkbox_confirmation"
  ) {

    return (

      <div className="flex items-center gap-2">

        <CheckCircleIcon
          className={`h-5 w-5 ${
            question.confirmationValue
              ? "text-lime-600"
              : "text-gray-400"
          }`}
        />

        <p className="text-xs font-semibold text-gray-700">

          {question.confirmationValue
            ? "Confirmed"
            : "Not confirmed"}

        </p>

      </div>

    )

  }


  return (

    <p className="text-xs text-gray-500">
      Response type: {questionType || "Unknown"}
    </p>

  )

}


/*
|--------------------------------------------------------------------------
| 06 - Question Card
|--------------------------------------------------------------------------
*/

const QuestionCard = ({
  question,
  index,
}) => {

  return (

    <div className="bg-white border border-gray-100 rounded-lg p-4">

      <div className="flex items-start gap-3">

        <span className="shrink-0 bg-orange-100 text-orange-700 rounded-full w-7 h-7 flex items-center justify-center text-[10px] font-bold">
          {question.questionNumber || index + 1}
        </span>

        <div className="flex-1 min-w-0">

          <p className="text-xs font-bold text-gray-800 leading-relaxed">
            {question.questionText}
          </p>

          {question.questionDescription && (

            <p className="text-[10px] text-gray-500 mt-2 leading-relaxed whitespace-pre-wrap">
              {question.questionDescription}
            </p>

          )}

          <div className="bg-orange-50/40 rounded-lg p-3 mt-3">

            <p className="text-[9px] font-bold uppercase text-orange-700 mb-2">
              My Response
            </p>

            <MyAnswer
              question={question}
            />

          </div>

        </div>

      </div>

    </div>

  )

}


/*
|--------------------------------------------------------------------------
| 07 - Personal Journey Response
|--------------------------------------------------------------------------
*/

const MyIntentionJourney = ({
  journey,
  index,
}) => {

  const [
    isExpanded,
    setIsExpanded,
  ] = useState(true)

  const board =
    journey?.intentionBoard || {}

  const response =
    journey?.response || {}

  const responsiveness =
    journey?.responsiveness || {}

  const timeline =
    journey?.timeline || {}

  const questions =
    journey?.questions || []

  const timezone =
    board.timezone


  const rankLabel =
    value =>
      Number.isInteger(value)
        ? `#${value}`
        : "Not available"


  return (

    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">

      {/* Journey Header */}

      <div className="bg-orange-50 p-4 sm:p-5">

        <div className="flex flex-wrap justify-between items-start gap-3">

          <div className="min-w-0">

            <p className="text-[9px] uppercase font-bold text-orange-700">
              My Intention {index + 1}
            </p>

            <p className="text-lg font-bold text-gray-900 mt-1">
              {board.boardName || "Intention Board"}
            </p>

            {board.headline && (

              <p className="text-xs text-gray-600 mt-2">
                {board.headline}
              </p>

            )}

          </div>

          <span
            className={`rounded-full px-3 py-1 text-[10px] font-bold ${
              journey.hasSubmittedResponse
                ? "bg-lime-100 text-lime-700"
                : "bg-gray-100 text-gray-600"
            }`}
          >

            {journey.hasSubmittedResponse
              ? "Submitted"
              : "No Submitted Response"}

          </span>

        </div>

        <button
          type="button"
          onClick={() =>
            setIsExpanded(
              previous => !previous
            )
          }
          className="flex items-center gap-1 text-[10px] font-bold text-orange-700 mt-4"
        >

          {isExpanded ? (

            <>
              <ChevronUpIcon className="w-4 h-4" />
              Hide My Intention
            </>

          ) : (

            <>
              <ChevronDownIcon className="w-4 h-4" />
              View My Intention
            </>

          )}

        </button>

      </div>


      {isExpanded && (

        <div className="p-4 sm:p-5 space-y-5">

          {!journey.hasSubmittedResponse ? (

            <div className="bg-gray-50 rounded-lg p-5">

              <p className="text-xs font-bold text-gray-700">
                No submitted Intention response is available.
              </p>

              <p className="text-[10px] text-gray-500 mt-2">
                Only final submitted responses are displayed here.
              </p>

            </div>

          ) : (

            <>

              {/* Submission */}

              <div>

                <div className="flex items-center gap-2 mb-3">

                  <ClipboardDocumentListIcon
                    className="w-5 h-5 text-orange-600"
                  />

                  <p className="text-sm font-bold text-gray-800">
                    My Submission
                  </p>

                </div>


                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">

                  <MetricCard
                    label="Submission Order"
                    value={rankLabel(
                      responsiveness.submissionRank
                    )}
                    note="Order of complete submissions."
                  />

                  <MetricCard
                    label="Completion Speed Rank"
                    value={rankLabel(
                      responsiveness.completionSpeedRank
                    )}
                    note="Elapsed time from start to submission."
                  />

                  <MetricCard
                    label="Start to Submission"
                    value={formatDuration(
                      responsiveness.startToSubmitMinutes
                    )}
                  />

                  <MetricCard
                    label="Deadline Lead"
                    value={formatDeadlineLead(
                      responsiveness.deadlineLeadMinutes
                    )}
                  />

                </div>


                <div className="bg-gray-50 rounded-lg p-4 mt-3">

                  <div className="flex flex-wrap justify-between gap-3">

                    <div>

                      <p className="text-[10px] font-bold text-gray-500">
                        Submitted At
                      </p>

                      <p className="text-xs font-semibold text-gray-800 mt-1">
                        {formatDateTime(
                          response.submittedAt,
                          timezone
                        )}
                      </p>

                    </div>


                    <div>

                      <p className="text-[10px] font-bold text-gray-500">
                        Submission Mode
                      </p>

                      <p className="text-xs font-semibold text-gray-800 mt-1">

                        {response.submissionMode === "automatic_deadline"
                          ? "Automatic at Deadline"
                          : "Manual Submission"}

                      </p>

                    </div>


                    <div>

                      <p className="text-[10px] font-bold text-gray-500">
                        Completion
                      </p>

                      <p className="text-xs font-semibold text-gray-800 mt-1">
                        {response.completionPercentage ?? 0}%
                      </p>

                    </div>

                  </div>

                </div>

              </div>


              {/* Responsiveness */}

              <div>

                <div className="flex items-center gap-2 mb-3">

                  <ClockIcon
                    className="w-5 h-5 text-orange-600"
                  />

                  <p className="text-sm font-bold text-gray-800">
                    My Responsiveness
                  </p>

                </div>


                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">

                  <MetricCard
                    label="Nomination to Access"
                    value={formatDuration(
                      responsiveness.nominationToAccessMinutes
                    )}
                  />

                  <MetricCard
                    label="Access to Start"
                    value={formatDuration(
                      responsiveness.accessToStartMinutes
                    )}
                  />

                  <MetricCard
                    label="Nomination to Submission"
                    value={formatDuration(
                      responsiveness.nominationToSubmitMinutes
                    )}
                  />

                  <MetricCard
                    label="Complete Submissions"
                    value={
                      responsiveness.totalCompleteSubmissions ?? 0
                    }
                    note="Total complete submissions on this board."
                  />

                </div>


                <div className="bg-gray-50 rounded-lg p-4 mt-3">

                  <p className="text-[10px] font-bold text-gray-600 mb-3">
                    My Response Timeline
                  </p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">

                    {[
                      {
                        label: "Nominated",
                        value: timeline.nominatedAt,
                      },
                      {
                        label: "First Accessed",
                        value: timeline.firstAccessedAt,
                      },
                      {
                        label: "Response Started",
                        value: timeline.responseStartedAt,
                      },
                      {
                        label: "Submitted",
                        value: timeline.submittedAt,
                      },
                    ].map(
                      item => (

                        <div
                          key={item.label}
                        >

                          <p className="text-[9px] font-bold text-gray-500">
                            {item.label}
                          </p>

                          <p className="text-[10px] text-gray-800 mt-1">
                            {formatDateTime(
                              item.value,
                              timezone
                            )}
                          </p>

                        </div>

                      )
                    )}

                  </div>

                </div>

              </div>


              {/* Questions and Responses */}

              <div>

                <div className="flex flex-wrap items-center justify-between gap-2 mb-3">

                  <div className="flex items-center gap-2">

                    <ClipboardDocumentListIcon
                      className="w-5 h-5 text-orange-600"
                    />

                    <p className="text-sm font-bold text-gray-800">
                      My Questions and Responses
                    </p>

                  </div>

                  <span className="text-[10px] text-gray-500">
                    {questions.length} questions
                  </span>

                </div>


                <div className="space-y-3">

                  {questions.length === 0 ? (

                    <p className="bg-gray-50 rounded p-4 text-xs text-gray-500">
                      Question details are not available.
                    </p>

                  ) : (

                    questions.map(
                      (question, questionIndex) => (

                        <QuestionCard
                          key={
                            question.intentionQuestionId ||
                            questionIndex
                          }
                          question={question}
                          index={questionIndex}
                        />

                      )
                    )

                  )}

                </div>

              </div>

            </>

          )}

        </div>

      )}

    </div>

  )

}


/*
|--------------------------------------------------------------------------
| 08 - Main Component
|--------------------------------------------------------------------------
*/

const EtUserMyIntention = () => {

  useTitle("My Intention")


  const {

    data: myIntentionData,

    isLoading,

    isFetching,

    isError,

    error,

    refetch,

  } =
    useGetMyOrgIntentionResponsesQuery()


  const journeys =
    myIntentionData?.journeys || []


  if (isLoading) {

    return (

      <div className="bg-white rounded-lg p-8 text-center">

        <PulseLoader color="#ea580c" size={5} />

        <p className="text-xs text-gray-500 mt-3">
          Loading your Intention...
        </p>

      </div>

    )

  }


  if (isError) {

    return (

      <div className="bg-white rounded-lg p-5 font-poppins">

        <p className="text-sm font-bold text-orange-700">
          Unable to load My Intention
        </p>

        <p className="text-xs text-gray-600 mt-2">
          {error?.data?.message ||
            "Your Intention information could not be retrieved."}
        </p>

        <button
          type="button"
          onClick={refetch}
          className="bg-orange-600 text-white rounded px-4 py-2 text-xs font-bold mt-3"
        >
          Try Again
        </button>

      </div>

    )

  }


  if (
    !myIntentionData?.exists ||
    journeys.length === 0
  ) {

    return (

      <div className="bg-white rounded-lg p-6 font-poppins">

        <p className="text-sm font-bold text-gray-800">
          My Intention
        </p>

        <p className="text-xs text-gray-500 mt-2">
          No linked Intention Board has been found for your Trinity account.
        </p>

      </div>

    )

  }


  return (

    <div className="font-poppins space-y-4">

      {/* Header */}

      <div className="bg-white rounded-lg p-4 sm:p-5">

        <div className="flex flex-wrap justify-between items-start gap-3">

          <div>

            <p className="text-xl font-bold text-gray-900">
              My Intention
            </p>

            <p className="text-xs italic text-gray-500 mt-1">
              Revisit what I shared when I began this journey.
            </p>

          </div>


          <button
            type="button"
            onClick={refetch}
            disabled={isFetching}
            className="inline-flex items-center gap-2 bg-orange-50 text-orange-700 rounded px-3 py-2 text-[10px] font-bold disabled:opacity-50"
          >

            <ArrowPathIcon
              className={`w-4 h-4 ${
                isFetching
                  ? "animate-spin"
                  : ""
              }`}
            />

            Refresh

          </button>

        </div>

      </div>


      {/* Journeys */}

      {journeys.map(
        (journey, index) => (

          <MyIntentionJourney
            key={
              journey?.candidate?.intentionCandidateId ||
              index
            }
            journey={journey}
            index={index}
          />

        )
      )}

    </div>

  )

}


export default EtUserMyIntention